import React, { useEffect, useState, useRef } from 'react';
import { FadeIn } from './FadeIn';
import { ArrowRight, BookOpen, Search, MessageSquare, ShieldCheck, DollarSign, Bot, CalendarCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { trackAddToCart } from '../utils/metaPixel';
function useCountUp(target: number, duration = 1800, startCounting: boolean) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let start = 0;
    const startTime = performance.now();
    function step(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }, [target, duration, startCounting]);
  return value;
}
export function FinalCTA() {
  const counterRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = counterRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.4
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const fiveYear = useCountUp(5000, 1600, isVisible);
  const tenYear = useCountUp(10000, 2200, isVisible);
  return <section className="py-12 md:py-20 bg-cream-50 overflow-hidden" data-id="element-49">
      <div className="max-w-3xl mx-auto px-4 md:px-6" data-id="element-50">
        <FadeIn data-id="element-51">
          <div className="text-center mb-8 md:mb-10" data-id="element-52">
            <h2 className="text-xl md:text-4xl mb-3 md:mb-4 leading-snug" data-id="element-53">
              Before Paying Your Next Medical Bill,
              <br className="md:hidden" data-id="element-54" /> Take A Quick Look First
            </h2>
            <div className="space-y-2 md:space-y-3 text-sm md:text-lg text-brown-800 max-w-[18rem] md:max-w-xl mx-auto" data-id="element-55">
              
              
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.15} data-id="element-56">
          <div ref={counterRef} className="text-center mb-8 md:mb-10" data-id="element-57">
            <p className="text-sm md:text-lg text-brown-800 mb-4 md:mb-5 leading-relaxed max-w-[18rem] md:max-w-xl mx-auto" data-id="element-58">
              If you overpay even{' '}
              <span className="font-bold text-brown-900" data-id="element-59">$1,000</span> per year…
            </p>

            <p className="text-xs md:text-sm text-brown-600 mb-3 md:mb-4 uppercase tracking-wider font-medium" data-id="element-60">
              That's:
            </p>

            <div className="flex justify-center gap-4 md:gap-8 mb-5 md:mb-6" data-id="element-61">
              <div className="bg-white rounded-xl shadow-sm border border-cream-200 px-4 py-3 md:px-6 md:py-4 min-w-[8rem] md:min-w-[10rem]" data-id="element-62">
                <p className="text-2xl md:text-4xl font-bold text-terracotta-600 tabular-nums" data-id="element-63">
                  ${fiveYear.toLocaleString()}
                </p>
                <p className="text-[11px] md:text-sm text-brown-500 mt-0.5" data-id="element-64">
                  over 5 years
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-cream-200 px-4 py-3 md:px-6 md:py-4 min-w-[8rem] md:min-w-[10rem]" data-id="element-65">
                <p className="text-2xl md:text-4xl font-bold text-red-600 tabular-nums" data-id="element-66">
                  ${tenYear.toLocaleString()}
                </p>
                <p className="text-[11px] md:text-sm text-brown-500 mt-0.5" data-id="element-67">
                  over 10 years
                </p>
              </div>
            </div>

            <p className="text-sm md:text-base text-brown-700 leading-relaxed max-w-[18rem] md:max-w-lg mx-auto mb-2" data-id="element-68">
              That's a family vacation. Emergency savings. Debt eliminated.
            </p>
            <p className="text-sm md:text-lg font-semibold text-brown-900" data-id="element-69">
              Overpaying quietly compounds.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-70">
          <div id="what-you-get" className="bg-brown-900 text-cream-50 rounded-2xl md:rounded-3xl p-5 md:p-12 text-center relative shadow-2xl" data-id="element-71">
            <div className="absolute top-0 left-0 w-full h-1.5 md:h-2 bg-gradient-to-r from-terracotta-500 to-terracotta-600" data-id="element-72"></div>

            <h2 className="text-xl md:text-4xl mb-1.5 md:mb-2 text-white leading-tight" data-id="element-73">
              What You Get Inside
            </h2>
            <p className="text-xs md:text-base text-terracotta-400 font-semibold mb-2 md:mb-4" data-id="element-74">
              The Negotiation Blueprint for Women Who Refuse to Be Overcharged
            </p>
            <p className="text-xs md:text-sm text-cream-200 mb-4 md:mb-6 max-w-[16rem] md:max-w-md mx-auto leading-relaxed" data-id="element-75">
              A step-by-step system to question, negotiate, and reduce medical
              bills with confidence.
            </p>

            <div className="text-left max-w-[16rem] md:max-w-md mx-auto space-y-2.5 md:space-y-3 mb-6 md:mb-8" data-id="element-76">
              {[{
              icon: BookOpen,
              title: 'Complete Negotiation Blueprint',
              desc: 'How billing works and how to use it to your advantage.'
            }, {
              icon: Search,
              title: 'Itemized Bill Leverage System',
              desc: 'Request, review, and audit charges before negotiating.'
            }, {
              icon: MessageSquare,
              title: 'Word-for-Word Scripts',
              desc: 'What to say for reductions, settlements, and adjustments.'
            }, {
              icon: ShieldCheck,
              title: 'Insurance Appeal Framework',
              desc: 'Challenge denied or underpaid claims.'
            }, {
              icon: DollarSign,
              title: 'Payment Plan Leverage',
              desc: 'Use timing and lump-sum offers to strengthen your position.'
            }, {
              icon: Bot,
              title: 'AI Expense Tracker Setup',
              desc: 'Organize, flag discrepancies, and negotiate from clarity.'
            }, {
              icon: CalendarCheck,
              title: '30-Day Action Plan',
              desc: 'A clear roadmap so you always know your next step.'
            }].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-77">
                  <div className="bg-brown-800 p-1 md:p-1.5 rounded-md flex-shrink-0 mt-0.5" data-id="element-78">
                    <item.icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-terracotta-400" data-id="element-79" />
                  </div>
                  <div data-id="element-80">
                    <p className="font-bold text-white text-xs md:text-sm" data-id="element-81">
                      {item.title}
                    </p>
                    <p className="text-cream-300 text-[11px] md:text-xs leading-relaxed" data-id="element-82">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="border-t border-brown-700 pt-4 md:pt-6 mb-4 md:mb-6 max-w-[16rem] md:max-w-md mx-auto" data-id="element-83">
              <p className="text-cream-200 text-xs md:text-sm leading-relaxed mb-1.5 md:mb-2" data-id="element-84">
                Everything structured. Everything practical. Built for busy moms
                who don't have time to decode billing codes.
              </p>
              <p className="text-white font-semibold text-sm md:text-base" data-id="element-85">
                Hospitals have systems.{' '}
                <span className="text-terracotta-400" data-id="element-86">Now you do too.</span>
              </p>
            </div>

            <a id="final-cta" href="https://www.cleverpockets.com/cart/44867243868296:1" target="_blank" rel="noopener noreferrer" onClick={e => {
            e.preventDefault();
            trackAddToCart();
            setTimeout(() => {
              window.open('https://www.cleverpockets.com/cart/44867243868296:1', '_blank', 'noopener,noreferrer');
            }, 300);
          }} className="inline-flex items-center justify-center px-6 py-4 md:px-8 md:py-5 text-base md:text-xl font-bold text-white bg-terracotta-500 rounded-full shadow-lg hover:bg-terracotta-600 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-87">
              Get My Blueprint
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-88" />
            </a>

            <div className="mt-4 md:mt-5 flex flex-col items-center gap-1.5 md:gap-2" data-id="element-89">
              <div className="flex items-center gap-2 md:gap-3" data-id="element-90">
                <span className="text-sm md:text-lg text-[#FAF6F1] line-through decoration-[#FAF6F1]/60 decoration-2" data-id="element-91">
                  $19.99
                </span>
                <span className="text-3xl md:text-4xl font-bold text-green-400" data-id="element-92">
                  $6.39
                </span>
                <span className="bg-green-500 text-white text-[10px] md:text-xs font-bold uppercase tracking-wide px-2 py-0.5 md:px-2.5 md:py-1 rounded-full" data-id="element-93">
                  68% Off
                </span>
              </div>
              <p className="text-cream-300 text-xs md:text-sm" data-id="element-94">
                Get immediate access with a one‑time, secure payment.
              </p>
            </div>

            <p className="mt-3 md:mt-4 text-cream-300 text-[11px] md:text-sm" data-id="element-95">
              Because responsible women deserve leverage. And leverage changes
              everything.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}